import { Alert, AlertColor, Box, Grid, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getSenderIdentities, resetDeleteSenderIdentity, selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { convertToHumanReadable } from '../../../utils/string-util';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import BadRequestIcon from '../../../icons/Badrequesterror.svg';
import Error500 from '../../../icons/505Error.svg';
import DeleteIcon from '../../../icons/delete_Icon.svg';
import { ConfirmSenderIdentityDeleteModal } from './ConfirmSenderIdentityDeleteModal';
import moment from 'moment';

export const SenderIdentitiesTable = () => {
  const dispatch = useDispatch();
  const { uuid: propertyGroupUuid } = useParams<{ uuid: string }>();
  const { SenderIdentities, isFetchingSenderIdentities, SenderIdentitiesFetchingError, isDeletingSenderIdentityFailed, isDeletingSenderIdentitySuccess, DeleteSenderIdentityError } =
    useSelector(selectPropertyGroupDetailsState);
  const [currentSenderIdentityUuid, setCurrentSenderIdentityUuid] = useState<string>();
  const [openConfirmSenderIdentityDeleteModal, setOpenConfirmSenderIdentityDeleteModal] = useState<boolean>(false);
  const [snackBar, setSnackBar] = useState<{ severity: AlertColor; message?: string } | null>(null);

  useEffect(() => {
    handleFetchSenderIndentities(propertyGroupUuid);
  }, [dispatch, propertyGroupUuid]);

  const handleFetchSenderIndentities = (propertyGroupUuid: string) => {
    dispatch(
      getSenderIdentities({
        propertyGroupUuid: propertyGroupUuid,
        searchSenderIdentityDto: {}
      }),
    );
  };

  const getErrorIcon = (statusCode: number | undefined) => {
    if (String(statusCode)[0] === '4') {
      return BadRequestIcon;
    } else {
      return Error500;
    }
  };

  const handleConfirmModal = () => {
    setCurrentSenderIdentityUuid(undefined);
    setOpenConfirmSenderIdentityDeleteModal(false);
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBar(null);
  };

  useEffect(() => {
    if (isDeletingSenderIdentitySuccess) {
      setSnackBar({
        severity: 'success',
        message: 'Sender identity removed.',
      });
      dispatch(resetDeleteSenderIdentity());
    }
  }, [dispatch, isDeletingSenderIdentitySuccess]);

  useEffect(() => {
    if (isDeletingSenderIdentityFailed && DeleteSenderIdentityError.statusCode) {
      setSnackBar({
        severity: 'error',
        message: DeleteSenderIdentityError.message,
      });
      dispatch(resetDeleteSenderIdentity());
    }
  }, [dispatch, isDeletingSenderIdentityFailed, DeleteSenderIdentityError]);

  return (
    <Box>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={!!snackBar} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={snackBar?.severity}>
          {snackBar?.message}
        </Alert>
      </Snackbar>
      {SenderIdentitiesFetchingError.statusCode ? (
        <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '50vh' }}>
          <Grid item display="flex" flexDirection="column" alignItems="center">
            <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
              <img src={getErrorIcon(SenderIdentitiesFetchingError?.statusCode)} alt={String(SenderIdentitiesFetchingError?.statusCode)} style={{ height: '200px', width: '200px' }} />
            </Grid>
            <Grid item justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '30px',
                  lineHeight: '23px',
                  color: '#2E82AF',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {SenderIdentitiesFetchingError?.statusCode}
              </Typography>
            </Grid>
            {SenderIdentitiesFetchingError?.statusCode === 404 && (
              <Grid mt={2} item justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '20px',
                    lineHeight: '23px',
                    color: '#9E9E9E',
                    textAlign: 'center',
                    marginTop: '10px',
                  }}
                >
                  Page not found.
                </Typography>
              </Grid>
            )}
            <Grid item justifyContent="center" mt={2} xs={8}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '23px',
                  color: '#9E9E9E',
                  textAlign: 'center',
                  marginTop: '10px',
                }}
              >
                {SenderIdentitiesFetchingError?.message?.length === 0 ? '' : SenderIdentitiesFetchingError?.message}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      ) : isFetchingSenderIdentities ? (
        <Stack
          justifyContent="center"
          alignItems="center"
          style={{
            height: '100px',
          }}
        >
          <SpinnerWithNoConfirmation />
        </Stack>
      ) : (
        <TableContainer style={{ flex: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="left" id="subTitle">
                  Email
                </TableCell>
                <TableCell align="left" id="subTitle">
                  Status
                </TableCell>
                <TableCell align="center" id="subTitle">
                  Enable Date
                </TableCell>
                <TableCell align="center" id="subTitle"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SenderIdentities.map((identity) => (
                <TableRow key={identity.Id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, m: 0 }}>
                  <TableCell component="th" scope="row" id="content">
                    {identity.Email}
                  </TableCell>
                  <TableCell align="left" id="content">
                    {convertToHumanReadable(identity.Status)}
                  </TableCell>
                  <TableCell align="center" id="content">
                    {identity.EnabledDate ? moment(identity.EnabledDate).format('MM/D/yyyy') : null}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={'Remove identity'} placement="top">
                      <img
                        src={DeleteIcon}
                        style={{ height: '20px', width: '20px', cursor: 'pointer' }}
                        alt="DeleteIcon"
                        onClick={() => {
                          setCurrentSenderIdentityUuid(identity.Id);
                          setOpenConfirmSenderIdentityDeleteModal(true);
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmSenderIdentityDeleteModal propertyGroupUuid={propertyGroupUuid} senderIdentityId={currentSenderIdentityUuid} open={openConfirmSenderIdentityDeleteModal} onClose={handleConfirmModal} />
    </Box>
  );
};
